/**
 * MonthlyReconReport  —  Monthly Reconciliation tab inside ClinicReport
 *
 * Reads vacation and clinic closure data to flag practitioners who had any
 * approved leave during the selected month, and to show a clinic-closure notice.
 *
 * Scheduling / vacation data read:
 *   GET /api/practitioner-vacations/?clinic=<id>
 *       → `practitioner_profile_PractitionerVacation`
 *       → Row fields used: practitioner, date_ranges. `status` is NOT a row-level field
 *         — it lives inside each `date_ranges` entry, so it must be read from the
 *         overlapping range (see FinancialReport's `activeVacationRange` for the daily
 *         equivalent and the full field list).
 *       → Only ranges with status 'approved' (or NULL, for legacy pre-migration-0031
 *         rows) count. Pending and denied requests do NOT count as leave.
 *       → A practitioner is flagged (visual indicator only) if ANY such range overlaps
 *         the selected month — not necessarily the full month.
 *
 *   GET /api/clinic-vacations/?clinic=<id>
 *       → `clinic_ClinicVacation`
 *       → `hasClinicClosure` is true if any closure date range overlaps the month.
 *       → Used for a visual informational banner only. Clinic closures carry no
 *         per-range status.
 *
 * Note: neither leave nor a clinic closure restricts what can be entered or submitted,
 * here or in the daily Financial Report. Both are visual flags only — production, hours
 * and costs are always submitted exactly as typed.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  FormSubmit,
  FormField,
  ListPractitioner,
  Modal,
  PaginatedTable,
  SummaryRow,
  SummaryBox,
  CostProductionBreakdownForm,
  OtherProfitTierInputs,
  OtherDeductionTierInputs,
} from "@common";
import { ConfirmModal, showSuccess, showError } from "@common";
import TotalMonthData from "@pages/reporting/TotalMonthData";
import DailyReportFiles from "@pages/reporting/DailyReportFiles";
import {
  useMonthlyCompensationsByClinic,
  useCreateMonthlyCompensationForClinic,
  useLabInvoiceAttachments,
  useReportClinicData,
  useReportFiles,
  useUpdateMonthlyCompensationForClinic,
  useReportTotalMonthPractitioners,
  useUpdatePractitionerByClinic,
  usePractitionerVacations,
  useClinicVacations,
} from "@api/hooks";
import {
  formatValueForForm,
  toNumber,
  isValidNum,
  isValidSignedNum,
  normalizeTypeForUi,
} from "@utils/utils";
import {
  sortPractitionersDdsThenHyg,
  getUiTypeFromPractitioner,
  isFinancialModuleType,
} from "@utils/practitionerUtils";
import {
  parseOtherProfit,
  parseOtherDeduction,
  resolveOtherDeductionTiers,
  otherDeductionInputFromSaved,
  buildOtherDeductionEntry,
} from "@utils/practitionerFormUtils";
import {
  FORM_IDS,
  isObserver,
  isOrganizerOrAdmin,
  isAreaManager,
  isPayroll,
  isPractitionerLead,
  torontoMonthKey,
} from "@utils";
import { pickSelectedPractitionerRow, mapTotalsFromRow } from "@utils/monthTotalsUtils";
import { ScrollBox, PractitionerQuickLookModal } from "@common";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import Filter from "@components/common/Filter";
import { useAuth } from "@api/useAuth";
import { getMonthlyReconColumns, buildMonthlyReconTotalRow } from "@utils/monthlyReconciliation";
import { formatMoney } from "@utils/format";

const PRACTITIONER_TYPE_TO_BACKEND = {
  DDS: "dds",
  HYG: "hyg",
};

function getMonthDateRange(month) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(month || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return {
    date_from: `${match[1]}-${match[2]}-01`,
    date_to: `${match[1]}-${match[2]}-${String(lastDay).padStart(2, "0")}`,
  };
}

function amountToCents(value) {
  const number = Number(
    String(value ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(number) ? Math.round(number * 100) : null;
}

function getNestedTotal(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Number(value.total || 0);
  }
  return Number(value || 0);
}

const MONTHLY_PDF_BREAKDOWN_STATUSES = new Set(["suggested", "manual_review", "applied"]);

function getAttachmentPdfAmount(attachment) {
  if (attachment.total_amount !== null && attachment.total_amount !== undefined) {
    return Number(attachment.total_amount || 0);
  }
  if (attachment.extracted_lab_cost !== null && attachment.extracted_lab_cost !== undefined) {
    return Number(attachment.extracted_lab_cost || 0);
  }
  if (
    attachment.breakdown_total_amount !== null &&
    attachment.breakdown_total_amount !== undefined
  ) {
    return Number(attachment.breakdown_total_amount || 0);
  }
  return null;
}

function getMonthlyEligibleBreakdowns(attachment, clinic, dateRange, practitionerId = null) {
  if (attachment.reconciliation_status !== "matched") return [];

  return (attachment.breakdowns || [])
    .map((breakdown) => {
      const assignedDate = breakdown.assigned_date || attachment.assigned_date || "";
      const breakdownClinic = String(breakdown.clinic || attachment.clinic || "").toUpperCase();
      const resolvedPractitionerId = breakdown.practitioner_id || attachment.practitioner_id;
      return {
        ...breakdown,
        assigned_date: assignedDate,
        clinic: breakdownClinic,
        practitioner_id: resolvedPractitionerId,
        attachment_filename: breakdown.attachment_filename || attachment.original_filename,
      };
    })
    .filter((breakdown) => {
      if (!MONTHLY_PDF_BREAKDOWN_STATUSES.has(breakdown.status)) return false;
      if (breakdown.clinic !== clinic) return false;
      if (!breakdown.practitioner_id) return false;
      if (practitionerId && String(breakdown.practitioner_id) !== String(practitionerId))
        return false;
      if (
        !breakdown.assigned_date ||
        breakdown.assigned_date < dateRange.date_from ||
        breakdown.assigned_date > dateRange.date_to
      ) {
        return false;
      }
      return breakdown.total_amount !== null && breakdown.total_amount !== undefined;
    });
}

function attachmentMatchesMonthlyFallback(attachment, clinic, dateRange, practitionerId = null) {
  const attachmentClinic = String(attachment.clinic || "").toUpperCase();
  const assignedDate = attachment.assigned_date;
  const resolvedPractitionerId = attachment.practitioner_id;

  if (attachment.reconciliation_status !== "matched") return false;
  if (attachmentClinic !== clinic) return false;
  if (!resolvedPractitionerId) return false;
  if (practitionerId && String(resolvedPractitionerId) !== String(practitionerId)) return false;
  if (!assignedDate || assignedDate < dateRange.date_from || assignedDate > dateRange.date_to) {
    return false;
  }
  return getAttachmentPdfAmount(attachment) !== null;
}

function buildMonthlyPdfLabCostMap(labInvoiceData, clinicId, dateRange) {
  if (!dateRange) return new Map();
  const clinic = String(clinicId || "").toUpperCase();
  const attachments = labInvoiceData?.results || [];
  const totals = new Map();

  attachments.forEach((attachment) => {
    const eligibleBreakdowns = getMonthlyEligibleBreakdowns(attachment, clinic, dateRange);
    if (eligibleBreakdowns.length > 0) {
      eligibleBreakdowns.forEach((breakdown) => {
        const key = String(breakdown.practitioner_id);
        totals.set(key, (totals.get(key) || 0) + Number(breakdown.total_amount || 0));
      });
      return;
    }

    if (!attachmentMatchesMonthlyFallback(attachment, clinic, dateRange)) return;
    const key = String(attachment.practitioner_id);
    totals.set(key, (totals.get(key) || 0) + getAttachmentPdfAmount(attachment));
  });

  return totals;
}

function buildMonthlyPdfLabCostDailyRows({
  labInvoiceData,
  dailyReportsData,
  clinicId,
  dateRange,
  practitionerId,
}) {
  if (!dateRange || !practitionerId) return [];
  const clinic = String(clinicId || "").toUpperCase();
  const pid = String(practitionerId);
  const pdfByDate = new Map();
  const dailyByDate = new Map();

  (labInvoiceData?.results || []).forEach((attachment) => {
    const eligibleBreakdowns = getMonthlyEligibleBreakdowns(attachment, clinic, dateRange, pid);
    if (eligibleBreakdowns.length > 0) {
      eligibleBreakdowns.forEach((breakdown) => {
        const current = pdfByDate.get(breakdown.assigned_date) || {
          date: breakdown.assigned_date,
          pdfAmount: 0,
          invoiceCount: 0,
          filenames: new Set(),
        };
        current.pdfAmount += Number(breakdown.total_amount || 0);
        if (breakdown.attachment_filename) current.filenames.add(breakdown.attachment_filename);
        current.invoiceCount = current.filenames.size;
        pdfByDate.set(breakdown.assigned_date, current);
      });
      return;
    }

    if (!attachmentMatchesMonthlyFallback(attachment, clinic, dateRange, pid)) return;

    const current = pdfByDate.get(attachment.assigned_date) || {
      date: attachment.assigned_date,
      pdfAmount: 0,
      invoiceCount: 0,
      filenames: new Set(),
    };
    current.pdfAmount += getAttachmentPdfAmount(attachment);
    current.invoiceCount += 1;
    if (attachment.original_filename) current.filenames.add(attachment.original_filename);
    pdfByDate.set(attachment.assigned_date, current);
  });

  (dailyReportsData?.reports || []).forEach((report) => {
    if (String(report.practitioner_id || "") !== pid) return;
    if (!report.date) return;
    const current = dailyByDate.get(report.date) || 0;
    dailyByDate.set(report.date, current + getNestedTotal(report.lab_costs));
  });

  return [...new Set([...pdfByDate.keys(), ...dailyByDate.keys()])].sort().map((date) => {
    const pdf = pdfByDate.get(date) || { pdfAmount: 0, invoiceCount: 0, filenames: new Set() };
    const dailyAmount = dailyByDate.get(date) || 0;
    const difference = pdf.pdfAmount - dailyAmount;
    return {
      date,
      pdfAmount: pdf.pdfAmount,
      dailyAmount,
      difference,
      invoiceCount: pdf.invoiceCount,
      filenames: [...pdf.filenames],
      isMismatch: amountToCents(pdf.pdfAmount) !== amountToCents(dailyAmount),
    };
  });
}

/**
 * The month's file attachments — the same list and upload modal as the daily
 * Financial Report's Files tab, filed against the clinic-month instead.
 */
function MonthlyFilesPanel({ clinicId, month }) {
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const firstOfMonth = getMonthDateRange(month)?.date_from;
  // Shares its cache with the list below, so the count costs no extra request.
  const { data } = useReportFiles({ clinic: clinicId, date: firstOfMonth, period: "month" });
  if (!clinicId || !firstOfMonth) return null;

  const [year, monthNumber] = month.split("-").map(Number);
  const monthLabel = new Date(year, monthNumber - 1, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <section className="mb-5 rounded-lg border border-palette-slate-divider bg-palette-slate-soft px-4 py-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-palette-slate-divider pb-2">
        <div className="text-sm font-medium text-palette-indigo-badge">
          Files
          <span className="ml-1.5 rounded bg-palette-indigo-soft px-1.5 py-0.5 text-[11px] text-palette-indigo-badge">
            {data?.results?.length ?? 0}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-palette-slate-secondary">
            {clinicId} · {monthLabel}
          </span>
          <Button type="button" variant="primary" size="sm" onClick={() => setIsUploadOpen(true)}>
            Upload files
          </Button>
        </div>
      </div>
      <DailyReportFiles
        clinicId={clinicId}
        date={firstOfMonth}
        period="month"
        periodLabel={monthLabel}
        isUploadOpen={isUploadOpen}
        onUploadOpenChange={setIsUploadOpen}
      />
    </section>
  );
}

function ApprovalBadge({ label, approved }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        approved
          ? "bg-palette-green-muted text-palette-green-text border border-palette-green-border"
          : "bg-palette-gray-subtle text-palette-gray-badge border border-palette-gray-divider"
      }`}
    >
      <span aria-hidden="true">{approved ? "✓" : "•"}</span>
      {label}: {approved ? "Approved" : "Pending"}
    </span>
  );
}

function toBackendPractitionerType(value) {
  const uiType = normalizeTypeForUi(value);
  if (uiType.length === 0) return "";

  if (Object.prototype.hasOwnProperty.call(PRACTITIONER_TYPE_TO_BACKEND, uiType)) {
    return PRACTITIONER_TYPE_TO_BACKEND[uiType];
  }

  return uiType.toLowerCase();
}

export default function MonthlyReconReport({
  clinicId,
  month,
  canOverrideMonth = false,
  role = null,
  clinicData,
  practitionersData,
  hiddenColumns = [],
  onOpenDailyDate,
}) {
  const createMonthlyComp = useCreateMonthlyCompensationForClinic();
  const updateMonthlyComp = useUpdateMonthlyCompensationForClinic();
  const { user } = useAuth();

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.MONTHLY_RECONCILIATION_REPORT,
  );

  const isAdminOrOrganizer = isOrganizerOrAdmin(role);
  const isAreaManagerUser = isAreaManager(role);
  const isPayrollUser = isPayroll(role);
  const isPractitionerLeadUser = isPractitionerLead(role);

  const canEditPractitioner = isOrganizerOrAdmin(role);

  const [quickLookOpen, setQuickLookOpen] = useState(false);
  const [activePractitioner, setActivePractitioner] = useState(null);

  function openQuickLook(p) {
    setActivePractitioner(p);
    setQuickLookOpen(true);
  }

  const updatePractitionerMut = useUpdatePractitionerByClinic();

  // eslint-disable-next-line no-unused-vars
  async function handleUpdatePractitioner(original, payload) {
    if (!canEditPractitioner) throw new Error("No permission");
    if (!clinicId) throw new Error("Missing clinic id");

    await updatePractitionerMut.mutateAsync({
      clinicCode: clinicId,
      id: original.id,
      data: payload,
      method: "patch",
    });
  }

  const roster = useMemo(() => {
    const items = practitionersData?.results || practitionersData || [];
    const list = Array.isArray(items) ? items : [];
    // Temporary hold: Monthly Reconciliation shows only Dentists + Hygienists.
    return list.filter((p) => p.status !== "inactive" && isFinancialModuleType(p));
  }, [practitionersData]);

  const [typeFilter, setTypeFilter] = useState("ALL");

  const filteredRoster = useMemo(() => {
    let list = roster;
    if (typeFilter === "DDS" || typeFilter === "HYG") {
      list = roster.filter((p) => getUiTypeFromPractitioner(p) === typeFilter);
    }
    return sortPractitionersDdsThenHyg(list);
  }, [roster, typeFilter]);

  // Fetch vacations for the whole clinic, then compute which practitioner IDs
  // have any vacation that overlaps the selected month.
  // month is "YYYY-MM" — we check if any date_range intersects [monthStart, monthEnd).
  const { data: vacationsData } = usePractitionerVacations({ clinic: clinicId });
  const { data: clinicVacationsData } = useClinicVacations({ clinic: clinicId });

  const onVacationIds = useMemo(() => {
    const vacations = vacationsData?.results ?? vacationsData ?? [];
    const [y, m] = month.split("-").map(Number);
    const monthStart = `${month}-01`;
    const nextMonthStart =
      m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const ids = new Set();
    for (const p of roster) {
      const onVacation = vacations.some((vac) => {
        return (
          vac.practitioner === p.id &&
          (vac.date_ranges ?? []).some(
            (r) =>
              r.start_date < nextMonthStart &&
              r.end_date >= monthStart &&
              // status is per-range, not per-row. Reading it off the row yielded
              // undefined, so pending and denied requests were flagging practitioners
              // too. null still counts as approved for legacy pre-0031 rows.
              (r.status === "approved" || r.status == null),
          )
        );
      });
      if (onVacation) ids.add(p.id);
    }
    return ids;
  }, [vacationsData, roster, month]);

  // True when ANY clinic closure overlaps the selected month — shown as a visual indicator
  const hasClinicClosure = useMemo(() => {
    const closures = clinicVacationsData?.results ?? [];
    const [y, m] = month.split("-").map(Number);
    const monthStart = `${month}-01`;
    const nextMonthStart =
      m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
    return closures.some((cv) =>
      (cv.date_ranges ?? []).some((r) => r.start_date < nextMonthStart && r.end_date >= monthStart),
    );
  }, [clinicVacationsData, month]);

  const [activePid, setActivePid] = useState("");

  const validPid = activePid && String(activePid).trim() !== "" ? parseInt(activePid) : null;
  const totalMonthQuery = useReportTotalMonthPractitioners({
    clinic: clinicId,
    month,
    practitioners: validPid ? [validPid] : [],
  });
  const monthDateRange = useMemo(() => getMonthDateRange(month), [month]);
  const labInvoiceMonthlyRes = useLabInvoiceAttachments(
    {
      clinic: clinicId,
      assigned: true,
      date_from: monthDateRange?.date_from,
      date_to: monthDateRange?.date_to,
    },
    {
      enabled: Boolean(clinicId && monthDateRange),
      enablePolling: true,
    },
  );
  const monthlyPdfLabCostByPractitioner = useMemo(
    () => buildMonthlyPdfLabCostMap(labInvoiceMonthlyRes.data, clinicId, monthDateRange),
    [labInvoiceMonthlyRes.data, clinicId, monthDateRange],
  );
  const monthlyDailyReportsRes = useReportClinicData(
    clinicId,
    {
      date_from: monthDateRange?.date_from,
      date_to: monthDateRange?.date_to,
    },
    {
      enabled: Boolean(clinicId && monthDateRange),
      enablePolling: true,
    },
  );

  useEffect(() => {
    if (filteredRoster.length === 0) {
      if (activePid !== "") setActivePid("");
      return;
    }

    if (activePid === "") {
      setActivePid(filteredRoster[0].id);
      return;
    }

    let found = false;
    for (let i = 0; i < filteredRoster.length; i++) {
      if (String(filteredRoster[i].id) === String(activePid)) {
        found = true;
        break;
      }
    }
    if (!found) setActivePid(filteredRoster[0].id);
  }, [filteredRoster, activePid]);

  const selectedPractitioner = useMemo(() => {
    return roster.find((p) => String(p.id) === String(activePid)) || null;
  }, [roster, activePid]);

  const practitionerName = selectedPractitioner?.name || "";
  const practitionerTypeUi = useMemo(
    () => normalizeTypeForUi(selectedPractitioner?.practitioner_type),
    [selectedPractitioner],
  );

  // Other-profit tiers configured on the practitioner's profile.
  // Each tier carries { label, percent } — for each we'll collect a per-month
  // `input_value` and compute `profit = input_value * percent / 100`.
  const otherProfitTiers = useMemo(() => {
    const parsed = parseOtherProfit(selectedPractitioner?.other_profit);
    return parsed.enabled ? parsed.rows : [];
  }, [selectedPractitioner]);
  const hasOtherProfitTiers = otherProfitTiers.length > 0;

  // Other-deduction tiers configured on the practitioner. Each tier carries
  // { label, mode, hour_rate }: an `hourly` tier asks for per-month `hours` and
  // the backend computes `amount = hour_rate * hours`; an `amount` tier asks
  // for the dollar figure directly. Either way the amount is subtracted from
  // the basis BEFORE the associate-fee % is applied.
  const profileOtherDeductionTiers = useMemo(() => {
    const parsed = parseOtherDeduction(selectedPractitioner?.other_deduction);
    return parsed.enabled ? parsed.rows : [];
  }, [selectedPractitioner]);

  // Per-day flag is configured on the practitioner profile. When enabled,
  // surface a numeric Per Day input (e.g. total days worked in the month).
  const isPerDayEnabled = !!selectedPractitioner?.per_day;

  let listFilters = { month };
  if (isAreaManagerUser || isPractitionerLeadUser) {
    listFilters = { month, practitioner_id: activePid };
  }

  const monthlyListRes = useMonthlyCompensationsByClinic(clinicId, listFilters, {
    enablePolling: true,
  });

  // Targeted query: always fetch exactly the selected practitioner's record.
  // The main list query above is paginated and can miss the current record on
  // large clinics, causing the form to show "Create" when "Update" is correct.
  const targetedRes = useMonthlyCompensationsByClinic(
    clinicId,
    { month, practitioner_id: activePid },
    { enabled: !!activePid },
  );

  let monthlyRecords = [];
  if (monthlyListRes && monthlyListRes.data) {
    if (Array.isArray(monthlyListRes.data)) monthlyRecords = monthlyListRes.data;
    else if (Array.isArray(monthlyListRes.data.results))
      monthlyRecords = monthlyListRes.data.results;
  }
  const monthlyRecordsWithPdfLabCosts = monthlyRecords.map((record) => ({
    ...record,
    pdf_lab_cost: {
      total: monthlyPdfLabCostByPractitioner.get(String(record.practitioner_id)) || 0,
    },
  }));
  const selectedPdfLabCost = monthlyPdfLabCostByPractitioner.get(String(activePid)) || 0;
  const selectedPdfLabCostComment =
    selectedPdfLabCost > 0 ? "From assigned PDF invoice totals this month." : "";

  let targetedRecords = [];
  if (targetedRes && targetedRes.data) {
    if (Array.isArray(targetedRes.data)) targetedRecords = targetedRes.data;
    else if (Array.isArray(targetedRes.data.results)) targetedRecords = targetedRes.data.results;
  }

  const currentMonth = torontoMonthKey();
  const isCurrentMonth = month === currentMonth;

  // Submitting for the in-progress month is allowed; we only show a
  // warning so the user knows the data may still change.
  const dp102Blocked = false;

  // Fallback: when a 409 response includes the existing record, store it here
  // so the form can switch to Update mode without waiting for a query refetch.
  const [conflictRecord, setConflictRecord] = useState(null);

  // Clear the conflict record whenever the practitioner or month changes.
  useEffect(() => {
    setConflictRecord(null);
  }, [activePid, month]);

  // Use the targeted (practitioner-scoped) result for existingReport so it is
  // always accurate — the broad list query may truncate due to pagination.
  // Fall back to conflictRecord if the query hasn't resolved yet.
  let existingReport = null;
  for (let i = 0; i < targetedRecords.length; i++) {
    const r = targetedRecords[i];
    const samePractitioner = r.practitioner_id && String(r.practitioner_id) === String(activePid);
    const rTypeUi = normalizeTypeForUi(r.practitioner_type);
    const sameType = rTypeUi === "" || rTypeUi === practitionerTypeUi;
    if (samePractitioner && sameType) {
      existingReport = r;
      break;
    }
  }
  if (!existingReport && conflictRecord) {
    existingReport = conflictRecord;
  }

  // A month already on the books keeps the mode and rate its entries were
  // saved with: changing the practitioner's config must not reinterpret or
  // re-price a figure another month already recorded. Memoized because the
  // prefill effect below depends on this identity — rebuilding it every render
  // would re-seed the inputs and stamp on whatever is being typed.
  const savedOtherDeduction = existingReport?.other_deduction;
  const otherDeductionTiers = useMemo(
    () => resolveOtherDeductionTiers(profileOtherDeductionTiers, savedOtherDeduction),
    [profileOtherDeductionTiers, savedOtherDeduction],
  );
  const hasOtherDeductionTiers = otherDeductionTiers.length > 0;

  const hasSubmittedRecord = !!existingReport?.id;
  const isAdminApproved = !!(
    existingReport?.admin_approval && existingReport.admin_approval.approved
  );
  const isAmApproved = !!(existingReport?.am_approval && existingReport.am_approval.approved);
  const isPayrollApproved = !!(
    existingReport?.payroll_approval && existingReport.payroll_approval.approved
  );
  const isApproved = isAdminApproved;
  const isUpdate = hasSubmittedRecord;

  // Pending "please reset all approvals" request, raised by a non-admin
  // approver. The backend rejects non-admins from clearing other approval
  // levels directly, so AM/Payroll/PL flag a request here and an admin must
  // accept it before the AM → Payroll → Admin pipeline is reset.
  const resetRequest = existingReport?.request_reset_approval || {};
  const hasResetRequest = !!resetRequest.status;
  const resetRequestUser = resetRequest.user_request || "";

  const canPractitionerLeadCreate = isPractitionerLeadUser && !hasSubmittedRecord;
  const canPractitionerLeadEdit = isPractitionerLeadUser && hasSubmittedRecord && !isApproved;

  const canAreaManagerCreate = isAreaManagerUser && !hasSubmittedRecord;
  const canAreaManagerEdit = isAreaManagerUser && hasSubmittedRecord && !isApproved;
  // AM / Payroll can still stamp their own approval column even after admin
  // signed off — admin approval only locks form-data edits, not the per-role
  // approval pipeline.
  const canAreaManagerApprove = isAreaManagerUser && hasSubmittedRecord && !isAmApproved;
  const canPayrollApprove = isPayrollUser && hasSubmittedRecord && !isPayrollApproved;
  const canAdminApprove = isAdminOrOrganizer && hasSubmittedRecord;

  const canAdminCreate = isAdminOrOrganizer && !hasSubmittedRecord;
  const canAdminEdit = isAdminOrOrganizer && hasSubmittedRecord;

  const canSaveForm =
    canPractitionerLeadCreate ||
    canPractitionerLeadEdit ||
    canAreaManagerCreate ||
    canAreaManagerEdit ||
    canAdminCreate ||
    canAdminEdit;

  let reportedIds = [];
  if (isAdminOrOrganizer || isObserver(role)) {
    for (let i = 0; i < roster.length; i++) {
      const p = roster[i];
      let submitted = false;

      for (let j = 0; j < monthlyRecords.length; j++) {
        const r = monthlyRecords[j];
        if (r.practitioner_id && String(r.practitioner_id) === String(p.id)) {
          submitted = true;
          break;
        }
      }

      if (submitted) reportedIds.push(p.id);
    }
  } else {
    if (existingReport) reportedIds = [activePid];
  }

  const [vals, setVals] = useState({
    production: "",
    collection: "",
    lab: "",
    material: "",
    invisalign: "",
    anesthesia_fee: "",
    anesthesiologist_fee: "",
    facility_fee: "",
    botox: "",
    production_adjustment: "",
    collection_adjustment: "",
    anesthesiologist_production: "",
    facility_production: "",
    hours: "",
  });
  // Monthly reconciliation requires the entered Lab Costs to match the assigned
  // PDF lab-cost totals for the month before the report can be submitted.
  const labCostMatchesPdf =
    amountToCents(vals.lab || "0") === amountToCents(selectedPdfLabCost.toFixed(2));
  const selectedPdfLabCostWarning = labCostMatchesPdf
    ? ""
    : `Monthly Lab Costs must equal PDF Lab Costs (${formatMoney(selectedPdfLabCost)}) before submitting.`;
  const selectedPdfLabCostDailyRows = useMemo(
    () =>
      buildMonthlyPdfLabCostDailyRows({
        labInvoiceData: labInvoiceMonthlyRes.data,
        dailyReportsData: monthlyDailyReportsRes.data,
        clinicId,
        dateRange: monthDateRange,
        practitionerId: activePid,
      }),
    [labInvoiceMonthlyRes.data, monthlyDailyReportsRes.data, clinicId, monthDateRange, activePid],
  );
  const selectedPdfLabCostMismatchRows = useMemo(
    () => selectedPdfLabCostDailyRows.filter((row) => row.isMismatch),
    [selectedPdfLabCostDailyRows],
  );
  const selectedDailyLabCostTotal = selectedPdfLabCostDailyRows.reduce(
    (total, row) => total + Number(row.dailyAmount || 0),
    0,
  );
  const [includedAdjustmentDeduction, setIncludedAdjustmentDeduction] = useState(false);

  const emptyBreakdowns = () => ({
    production: [],
    collection: [],
    lab: [],
    material: [],
    invisalign: [],
    anesthesia_fee: [],
    anesthesiologist_fee: [],
    facility_fee: [],
    botox: [],
    production_adjustment: [],
    collection_adjustment: [],
    hours: [],
  });
  const [breakdowns, setBreakdowns] = useState(emptyBreakdowns());

  function computeBreakdownTotal(entries) {
    // Single-entry: preserve the raw string so mid-type decimals (e.g. "25.")
    // and partial negatives (e.g. "-", "-.") aren't stripped while typing.
    if (entries.length === 1) {
      const raw = entries[0];
      if (raw === "" || raw == null) return "";
      const s = String(raw).trim();
      if (s === "-" || s === "-." || s === "." || s === "-0") return s;
      const n = parseFloat(s);
      return Number.isNaN(n) ? "" : s;
    }
    const nums = entries.map((e) => parseFloat(e)).filter((n) => !Number.isNaN(n));
    if (nums.length === 0) return "";
    const sum = nums.reduce((s, n) => s + n, 0);
    return String(parseFloat(sum.toFixed(2)));
  }

  function handleBreakdownChange(field, entries) {
    setBreakdowns((prev) => ({ ...prev, [field]: entries }));
    setVals((prev) => ({ ...prev, [field]: computeBreakdownTotal(entries) }));
  }

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [showNegativeConfirm, setShowNegativeConfirm] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [reapproveOpen, setReapproveOpen] = useState(false);
  const [pdfLabCostMismatchOpen, setPdfLabCostMismatchOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(true);

  // Per-tier input_value strings, index-aligned with `otherProfitTiers`.
  const [otherProfitInputs, setOtherProfitInputs] = useState([]);
  // Per-tier input_value strings, index-aligned with `otherDeductionTiers`.
  const [otherDeductionInputs, setOtherDeductionInputs] = useState([]);
  // Per-day number selection ("" | "0.5" | "1").
  const [perDayNumber, setPerDayNumber] = useState("");

  useEffect(() => {
    const nextVals = {
      production: "",
      collection: "",
      lab: "",
      material: "",
      invisalign: "",
      anesthesia_fee: "",
      anesthesiologist_fee: "",
      facility_fee: "",
      botox: "",
      production_adjustment: "",
      collection_adjustment: "",
      anesthesiologist_production: "",
      facility_production: "",
      hours: "",
    };

    const toStringArr = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr.map(String) : []);

    // Seed form from saved record — ?.total for the number value, ?.breakdown for the 3 slots
    if (existingReport) {
      nextVals.production = formatValueForForm(existingReport.production?.total);
      nextVals.collection = formatValueForForm(existingReport.collection?.total);
      nextVals.lab = formatValueForForm(existingReport.lab_cost?.total);
      nextVals.material = formatValueForForm(existingReport.material_cost?.total);
      nextVals.invisalign = formatValueForForm(existingReport.invisalign_cost?.total);
      nextVals.anesthesia_fee = formatValueForForm(existingReport.anesthesia_fee?.total);
      nextVals.anesthesiologist_fee = formatValueForForm(
        existingReport.anesthesiologist_fee?.total,
      );
      nextVals.facility_fee = formatValueForForm(existingReport.facility_fee?.total);
      nextVals.botox = formatValueForForm(existingReport.botox?.total);
      nextVals.production_adjustment = formatValueForForm(
        existingReport.production_adjustment?.total,
      );
      nextVals.collection_adjustment = formatValueForForm(
        existingReport.collection_adjustment?.total,
      );
      nextVals.anesthesiologist_production = formatValueForForm(
        existingReport.anesthesiologist_production,
      );
      nextVals.facility_production = formatValueForForm(existingReport.facility_production);
      nextVals.hours = formatValueForForm(existingReport.hours_worked?.total);

      setBreakdowns({
        production: toStringArr(existingReport.production?.breakdown),
        collection: toStringArr(existingReport.collection?.breakdown),
        lab: toStringArr(existingReport.lab_cost?.breakdown),
        material: toStringArr(existingReport.material_cost?.breakdown),
        invisalign: toStringArr(existingReport.invisalign_cost?.breakdown),
        anesthesia_fee: toStringArr(existingReport.anesthesia_fee?.breakdown),
        anesthesiologist_fee: toStringArr(existingReport.anesthesiologist_fee?.breakdown),
        facility_fee: toStringArr(existingReport.facility_fee?.breakdown),
        botox: toStringArr(existingReport.botox?.breakdown),
        production_adjustment: toStringArr(existingReport.production_adjustment?.breakdown),
        collection_adjustment: toStringArr(existingReport.collection_adjustment?.breakdown),
        hours: toStringArr(existingReport.hours_worked?.breakdown),
      });
    } else {
      setBreakdowns(emptyBreakdowns());
    }

    setVals(nextVals);
    setIncludedAdjustmentDeduction(Boolean(existingReport?.included_adjustment_deduction));
    setHasSubmitted(false);
    setApproveOpen(false);

    // Seed per-tier input_values. If the existing record already has
    // `other_profit` entries, match by label so the user sees what they saved.
    const savedByLabel = new Map();
    const savedOther = Array.isArray(existingReport?.other_profit)
      ? existingReport.other_profit
      : [];
    savedOther.forEach((entry) => {
      if (entry && entry.label) savedByLabel.set(String(entry.label), entry);
    });
    setOtherProfitInputs(
      otherProfitTiers.map((t) => {
        const match = savedByLabel.get(String(t.label));
        const v =
          match && match.input_value !== undefined && match.input_value !== null
            ? match.input_value
            : "";
        return v === "" ? "" : formatValueForForm(v);
      }),
    );

    // Seed deduction inputs the same way.
    const savedByLabelDed = new Map();
    const savedDed = Array.isArray(existingReport?.other_deduction)
      ? existingReport.other_deduction
      : [];
    savedDed.forEach((entry) => {
      if (entry && entry.label) savedByLabelDed.set(String(entry.label), entry);
    });
    setOtherDeductionInputs(
      otherDeductionTiers.map((t) => {
        const v = otherDeductionInputFromSaved(t, savedByLabelDed.get(String(t.label)));
        return v === "" ? "" : formatValueForForm(v);
      }),
    );

    // Seed per-day number from existing record (if any).
    if (
      existingReport &&
      existingReport.per_day_number !== undefined &&
      existingReport.per_day_number !== null &&
      existingReport.per_day_number !== ""
    ) {
      setPerDayNumber(formatValueForForm(existingReport.per_day_number));
    } else {
      setPerDayNumber("");
    }

    if (hasSubmittedRecord) {
      setIsEditing(false);
      return;
    }

    if (canPractitionerLeadCreate || canAreaManagerCreate || canAdminCreate) {
      setIsEditing(true);
      return;
    }

    setIsEditing(false);
  }, [
    activePid,
    month,
    existingReport,
    hasSubmittedRecord,
    canPractitionerLeadCreate,
    canAreaManagerCreate,
    canAdminCreate,
    otherProfitTiers,
    otherDeductionTiers,
  ]);

  // When creating a new record, prefill Hours Worked from Total Data if > 0.
  useEffect(() => {
    if (hasSubmittedRecord) return;
    const row = pickSelectedPractitionerRow(totalMonthQuery.data, practitionerName);
    const totals = mapTotalsFromRow(row);
    const totalHours = Number(totals.total_hours_worked || 0);
    if (totalHours > 0) {
      setVals((prev) =>
        prev.hours === "" ? { ...prev, hours: formatValueForForm(totalHours) } : prev,
      );
    }
  }, [totalMonthQuery.data, practitionerName, hasSubmittedRecord, activePid, month]);

  const errors = {
    production: "",
    collection: "",
    lab: "",
    material: "",
    invisalign: "",
    anesthesia_fee: "",
    anesthesiologist_fee: "",
    facility_fee: "",
    botox: "",
    production_adjustment: "",
    collection_adjustment: "",
    anesthesiologist_production: "",
    facility_production: "",
    hours: "",
  };

  if (vals.production === "") errors.production = "Required";
  else if (!isValidSignedNum(vals.production)) errors.production = "Numbers only";

  if (vals.collection === "") errors.collection = "Required";
  else if (!isValidSignedNum(vals.collection)) errors.collection = "Numbers only";

  const optionalNumericFields = [
    "lab",
    "material",
    "invisalign",
    "anesthesia_fee",
    "anesthesiologist_fee",
    "facility_fee",
    "botox",
    "production_adjustment",
    "collection_adjustment",
    "anesthesiologist_production",
    "facility_production",
    "hours",
  ];
  optionalNumericFields.forEach((field) => {
    if (!isValidSignedNum(vals[field])) errors[field] = "Numbers only";
  });

  // Validate each other-profit tier input_value (optional, numeric).
  // Negative values are allowed so that corrections / clawbacks can be entered.
  const otherProfitErrors = otherProfitTiers.map((_, idx) => {
    const raw = otherProfitInputs[idx];
    if (raw === undefined || raw === null || raw === "") return "";
    return isValidSignedNum(raw) ? "" : "Numbers only";
  });
  const hasOtherProfitErrors = otherProfitErrors.some((e) => e !== "");

  // Validate each other-deduction tier input_value (optional, numeric, >=0).
  const otherDeductionErrors = otherDeductionTiers.map((_, idx) => {
    const raw = otherDeductionInputs[idx];
    if (raw === undefined || raw === null || raw === "") return "";
    return isValidNum(raw) ? "" : "Numbers only";
  });
  const hasOtherDeductionErrors = otherDeductionErrors.some((e) => e !== "");

  const hasErrors =
    Object.values(errors).some((err) => err !== "") ||
    hasOtherProfitErrors ||
    hasOtherDeductionErrors;

  let canSubmit = true;
  if (hasErrors) canSubmit = false;
  if (dp102Blocked) canSubmit = false;
  if (!isEditing) canSubmit = false;
  if (!canSaveForm) canSubmit = false;
  if (isPractitionerLeadUser && isApproved) canSubmit = false;
  if (isAreaManagerUser && isApproved) canSubmit = false;
  // Hard block: monthly Lab Costs must reconcile to the assigned PDF totals.
  // if (!labCostMatchesPdf) canSubmit = false;

  // cleanBreakdown maps each slot to a number, preserving position so slot indices survive the round-trip.
  // Empty slots become 0 (not filtered) so that DCP/DKJ values don't collapse to position 0 on reload.
  const cleanBreakdown = (entries) =>
    entries.map((e) => (e !== "" && e !== null ? parseFloat(e) || 0 : 0));

  const payload = {
    practitioner: parseInt(activePid),
    month,
    practitioner_type: toBackendPractitionerType(selectedPractitioner?.practitioner_type),

    production: toNumber(vals.production),
    collection: toNumber(vals.collection),

    lab_cost: toNumber(vals.lab),
    material_cost: toNumber(vals.material),
    invisalign_cost: toNumber(vals.invisalign),
    anesthesia_fee: toNumber(vals.anesthesia_fee),
    anesthesiologist_fee: toNumber(vals.anesthesiologist_fee),
    facility_fee: toNumber(vals.facility_fee),
    botox: toNumber(vals.botox),
    production_adjustment: toNumber(vals.production_adjustment),
    collection_adjustment: toNumber(vals.collection_adjustment),
    anesthesiologist_production:
      vals.anesthesiologist_production === "" ? null : toNumber(vals.anesthesiologist_production),
    facility_production:
      vals.facility_production === "" ? null : toNumber(vals.facility_production),

    included_adjustment_deduction: includedAdjustmentDeduction,

    hours_worked: toNumber(vals.hours),

    production_breakdown: cleanBreakdown(breakdowns.production),
    collection_breakdown: cleanBreakdown(breakdowns.collection),
    lab_cost_breakdown: cleanBreakdown(breakdowns.lab),
    material_cost_breakdown: cleanBreakdown(breakdowns.material),
    invisalign_cost_breakdown: cleanBreakdown(breakdowns.invisalign),
    anesthesia_fee_breakdown: cleanBreakdown(breakdowns.anesthesia_fee),
    anesthesiologist_fee_breakdown: cleanBreakdown(breakdowns.anesthesiologist_fee),
    facility_fee_breakdown: cleanBreakdown(breakdowns.facility_fee),
    botox_breakdown: cleanBreakdown(breakdowns.botox),
    production_adjustment_breakdown: cleanBreakdown(breakdowns.production_adjustment),
    collection_adjustment_breakdown: cleanBreakdown(breakdowns.collection_adjustment),
    hours_worked_breakdown: cleanBreakdown(breakdowns.hours),

    other_profit: otherProfitTiers.map((t, idx) => {
      const raw = otherProfitInputs[idx];
      const inputValue = raw === "" || raw === undefined || raw === null ? 0 : toNumber(raw);
      const percent = Number(t.percent) || 0;
      // Backend recomputes `profit` from input_value × percent on save —
      // sending it from the client would just be duplicated math.
      return {
        label: t.label,
        percent,
        input_value: inputValue,
      };
    }),

    // Backend resolves each entry's amount on save (rate x hours, or the
    // amount as entered).
    other_deduction: otherDeductionTiers.map((t, idx) =>
      buildOtherDeductionEntry(t, otherDeductionInputs[idx]),
    ),

    per_day_number:
      isPerDayEnabled && perDayNumber !== "" && !Number.isNaN(Number(perDayNumber))
        ? toNumber(perDayNumber)
        : null,
    per_day_value:
      isPerDayEnabled &&
      selectedPractitioner?.per_day_value !== undefined &&
      selectedPractitioner?.per_day_value !== null &&
      selectedPractitioner?.per_day_value !== ""
        ? toNumber(selectedPractitioner.per_day_value)
        : null,

    category: "monthly_reconciliation",
  };

  const isLoading = createMonthlyComp.isPending || updateMonthlyComp.isPending;
  const fieldsDisabled = !isEditing || dp102Blocked;

  function showApiError(err, fallback) {
    let msg = fallback;

    if (err && err.response && err.response.data) {
      const data = err.response.data;
      // If the server returns an HTML error page (Django 500), show a safe
      // fallback message instead of rendering raw HTML in the UI.
      if (typeof data === "string" && data.trim().startsWith("<")) {
        msg = fallback;
      } else if (typeof data === "string") {
        msg = data;
      } else if (data.detail) {
        msg = data.detail;
      } else {
        msg = JSON.stringify(data);
      }
    } else if (err && err.message) {
      msg = err.message;
    }

    showError(msg);
    console.error("API error:", err);
  }

  function refetchTotalsNow() {
    totalMonthQuery.refetch();
  }

  function afterSaveSuccess(message) {
    recordSubmitSuccess();
    showSuccess(message);
    setHasSubmitted(false);
    setIsEditing(false);
    refetchTotalsNow();
  }

  function doSave() {
    if (!canSubmit) return;

    if (!payload.practitioner_type) {
      showError("Practitioner type is missing or invalid");
      return;
    }

    if ((isPractitionerLeadUser || isAreaManagerUser) && isApproved) {
      showError("Approved forms cannot be edited.");
      return;
    }

    if (isUpdate) {
      updateMonthlyComp.mutate(
        { clinicCode: clinicId, id: existingReport.id, data: payload },
        {
          onSuccess: function () {
            afterSaveSuccess("Monthly entry updated successfully");
          },
          onError: function (err) {
            showApiError(err, "Failed to update monthly entry");
          },
        },
      );
      return;
    }

    createMonthlyComp.mutate(
      { clinicCode: clinicId, data: payload },
      {
        onSuccess: function () {
          afterSaveSuccess("Monthly entry created successfully");
        },
        onError: function (err) {
          if (err?.response?.status === 409) {
            const body = err.response.data;
            // Backend returns the full existing record on 409 — use it directly
            // so the form immediately switches to Update mode without any refetch.
            if (body?.id) {
              setConflictRecord(body);
              if (monthlyListRes?.refetch) monthlyListRes.refetch();
              showError("A record already exists — the form has switched to Update mode.");
              return;
            }
          }
          showApiError(err, "Failed to create monthly entry");
        },
      },
    );
  }

  // Only Area Managers can ask for a reset. Once requested, an Admin OR
  // Payroll user can accept it. Other roles (PL / Bookkeeper / Visitor) do
  // not participate in the reset workflow.
  const canRequestReapprove =
    hasSubmittedRecord && !hasResetRequest && isAreaManagerUser && isAmApproved;

  // Admin can reset directly when admin-approved, OR accept a pending
  // request. Payroll can only ACCEPT a pending request (not reset proactively).
  const canAdminResetApprovals =
    hasSubmittedRecord &&
    ((isAdminOrOrganizer && (isAdminApproved || hasResetRequest)) ||
      (isPayrollUser && hasResetRequest));

  function doRequestReapprove() {
    if (!canRequestReapprove || !existingReport?.id) return;

    // Non-admins cannot modify other approval levels, so PATCH only the
    // request flag. Admin will pick this up and accept the reset.
    updateMonthlyComp.mutate(
      {
        clinicCode: clinicId,
        id: existingReport.id,
        data: {
          request_reset_approval: {
            status: true,
            user_request: user?.email || "",
          },
        },
      },
      {
        onSuccess: function () {
          showSuccess("Reset request sent — waiting for admin approval");
          setReapproveOpen(false);
          setIsEditing(false);
          refetchTotalsNow();
        },
        onError: function (err) {
          showApiError(err, "Failed to request reapproval");
        },
      },
    );
  }

  function doAcceptResetApprovals() {
    if (!canAdminResetApprovals || !existingReport?.id) return;

    // Clear all three approval levels and the pending reset request in one
    // PATCH. Only Admin/Organizer or Payroll (when a request is pending) may
    // call this — the backend mirrors that rule.
    updateMonthlyComp.mutate(
      {
        clinicCode: clinicId,
        id: existingReport.id,
        data: {
          am_approval: { approved: false, user: "" },
          payroll_approval: { approved: false, user: "" },
          admin_approval: { approved: false, user: "" },
          request_reset_approval: { status: false, user_request: "" },
        },
      },
      {
        onSuccess: function () {
          showSuccess("Approvals reset — pipeline is back to pending");
          setReapproveOpen(false);
          setIsEditing(false);
          refetchTotalsNow();
        },
        onError: function (err) {
          showApiError(err, "Failed to reset approvals");
        },
      },
    );
  }

  function doApprove() {
    if ((!canAreaManagerApprove && !canPayrollApprove && !canAdminApprove) || !existingReport?.id)
      return;

    // Stamp the approval on the field that matches the approver's role so the
    // 3-step pipeline (AM → Payroll → Admin) on AssociateFee reflects who
    // actually approved. Previously every approver wrote to `admin_approval`,
    // which made AM / Payroll approvals show up as "Admin approved".
    let approvalField = "admin_approval";
    if (isAreaManagerUser) approvalField = "am_approval";
    else if (isPayrollUser) approvalField = "payroll_approval";

    // Non-admin approvers PATCH only the approval field. Sending the full
    // form payload would be rejected by the backend if admin already signed
    // off ("Cannot edit an admin-approved record"). Admin still PATCHes the
    // full payload so any pending form edits are persisted alongside the
    // approval stamp.
    const approvalData = isAdminOrOrganizer
      ? { ...payload, [approvalField]: { approved: true, user: user?.email || "" } }
      : { [approvalField]: { approved: true, user: user?.email || "" } };

    updateMonthlyComp.mutate(
      {
        clinicCode: clinicId,
        id: existingReport.id,
        data: approvalData,
      },
      {
        onSuccess: function () {
          showSuccess("Monthly entry approved successfully");
          setApproveOpen(false);
          setIsEditing(false);
          refetchTotalsNow();
        },
        onError: function (err) {
          showApiError(err, "Failed to approve monthly entry");
        },
      },
    );
  }

  const NEGATIVE_FIELD_LABELS = {
    production: "Production",
    collection: "Collection",
    lab: "Lab Costs",
    material: "Material Costs",
    invisalign: "Invisalign Costs",
    anesthesia_fee: "Anesthesia Fee",
    anesthesiologist_fee: "Anesthesiologist Fee",
    facility_fee: "Facility Fee",
    botox: "Botox",
    production_adjustment: "Production Adjustment",
    collection_adjustment: "Collection Adjustment",
    anesthesiologist_production: "Anesthesiologist Production",
    facility_production: "Facility Production",
    hours: "Hours Worked",
  };

  function getNegativeFields(values) {
    return Object.entries(values)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined && Number(v) < 0)
      .map(([k]) => NEGATIVE_FIELD_LABELS[k] || k);
  }

  function handleConfirmNegative() {
    setShowNegativeConfirm(false);
    if (pendingPayload) {
      setPendingPayload(null);
      doSave();
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    setHasSubmitted(true);

    if (!canSubmit) return;

    const negativeFields = getNegativeFields(vals);
    if (negativeFields.length > 0) {
      setPendingPayload(true);
      setShowNegativeConfirm(true);
      return;
    }

    doSave();
  }

  const formSummary = (
    <div className="text-left space-y-3">
      <div className="text-sm space-y-2">
        <SummaryRow label="Month" value={month} labelClassName="text-palette-gray-secondary" />
        <SummaryRow
          label="Practitioner"
          value={practitionerName + " (" + String(practitionerTypeUi).toLowerCase() + ")"}
          labelClassName="text-palette-gray-secondary"
        />
        {(hasSubmittedRecord || isAreaManagerUser) && (
          <SummaryRow
            label="Status"
            value={
              hasSubmittedRecord
                ? `AM: ${isAmApproved ? "Approved" : "Pending"} · Payroll: ${
                    isPayrollApproved ? "Approved" : "Pending"
                  } · Admin: ${isAdminApproved ? "Approved" : "Pending"}`
                : "No submission yet"
            }
            labelClassName="text-palette-gray-secondary"
          />
        )}
      </div>

      <SummaryBox>
        <SummaryRow label="Production" value={vals.production || "0"} />
        <SummaryRow label="Collection" value={vals.collection || "0"} />
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("lab_costs") && (
          <SummaryRow label="Lab Costs" value={vals.lab || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("material_costs") && (
          <SummaryRow label="Material Costs" value={vals.material || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("invisalign_costs") && (
          <SummaryRow label="Invisalign Costs" value={vals.invisalign || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("anesthesia_fee") && (
          <SummaryRow label="Anesthesia Fee" value={vals.anesthesia_fee || "0"} />
        )}
        {clinicId === "ON-K-KIT" && (
          <>
            <SummaryRow
              label="Anesthesiologist Production"
              value={vals.anesthesiologist_production || "0"}
            />
            <SummaryRow label="Facility Production" value={vals.facility_production || "0"} />
          </>
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("anesthesiologist_fee") && (
          <SummaryRow label="Anesthesiologist Fee" value={vals.anesthesiologist_fee || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("facility_fee") && (
          <SummaryRow label="Facility Fee" value={vals.facility_fee || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("botox") && (
          <SummaryRow label="Botox" value={vals.botox || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("production_adjustment") && (
          <SummaryRow label="Production Adjustment" value={vals.production_adjustment || "0"} />
        )}
        {practitionerTypeUi !== "HYG" && !hiddenColumns.includes("collection_adjustment") && (
          <SummaryRow label="Collection Adjustment" value={vals.collection_adjustment || "0"} />
        )}
        <SummaryRow label="Hours Worked" value={vals.hours || "0"} />
        {hasOtherProfitTiers &&
          otherProfitTiers.map((tier, idx) => {
            const raw = otherProfitInputs[idx] ?? "";
            return <SummaryRow key={`op-summary-${idx}`} label={tier.label} value={raw || "0"} />;
          })}
        {hasOtherDeductionTiers &&
          otherDeductionTiers.map((tier, idx) => {
            const raw = otherDeductionInputs[idx] ?? "";
            return <SummaryRow key={`od-summary-${idx}`} label={tier.label} value={raw || "0"} />;
          })}
        {isPerDayEnabled && (
          <SummaryRow label="Total Days Worked" value={perDayNumber === "" ? "-" : perDayNumber} />
        )}
      </SummaryBox>
    </div>
  );

  const approveMessage = (
    <div className="space-y-3">
      {formSummary}
      <div className="rounded-xl border border-palette-amber-border bg-palette-amber-soft p-3 text-palette-amber-primary text-sm">
        <div className="font-semibold">Warning</div>
        <div className="mt-1">Once approved, this cannot be edited</div>
      </div>
    </div>
  );

  let submitButtonText = isUpdate ? "Update" : "Create";
  if (isPractitionerLeadUser && !isUpdate) {
    submitButtonText = "Submit";
  } else if (isAreaManagerUser && !isUpdate) {
    submitButtonText = "Submit";
  }

  return (
    <>
      <Card className="mt-4">
        {/*
         * Practitioner quick look — read-only here (no PATCH from this screen).
         * Practitioner Lead: hideFinancialFields mirrors Profile/Financial report so PL never sees
         * compensation / insurance / grid in any practitioner popup on the clinic dashboard.
         */}
        <ConfirmModal
          isOpen={showNegativeConfirm}
          onClose={() => {
            setShowNegativeConfirm(false);
            setPendingPayload(null);
          }}
          onConfirm={handleConfirmNegative}
          title="Negative Values Detected"
          message={
            <div>
              <p className="mb-2">The following fields have negative values:</p>
              <ul className="list-disc list-inside mb-2 font-medium">
                {getNegativeFields(vals).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <p>Are you sure this is not a mistake?</p>
            </div>
          }
          confirmText="Yes, Submit"
          cancelText="Go Back"
          confirmVariant="primary"
        />

        <PractitionerQuickLookModal
          isOpen={quickLookOpen}
          onClose={() => {
            setQuickLookOpen(false);
            setActivePractitioner(null);
          }}
          practitioner={activePractitioner}
          canEdit={false}
          onSave={null}
          hideFinancialFields={isPractitionerLead(role)}
        />

        <MonthlyFilesPanel clinicId={clinicId} month={month} />

        {isCurrentMonth && (
          <div className="mb-4 rounded-xl border border-palette-amber-border bg-palette-amber-soft px-4 py-3 text-sm text-palette-amber-text">
            <div className="font-semibold">Current month is still in progress</div>
            <div className="mt-1">
              You can still submit, but values may change before the month is complete.
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={approveOpen && (isAreaManagerUser || isPayrollUser || isAdminOrOrganizer)}
          onClose={() => setApproveOpen(false)}
          onConfirm={doApprove}
          title="Approve Record"
          message={approveMessage}
          confirmText="Approve"
          cancelText="Cancel"
          confirmVariant="success"
          isLoading={isLoading}
        />

        <ConfirmModal
          isOpen={reapproveOpen && (canRequestReapprove || canAdminResetApprovals)}
          onClose={() => setReapproveOpen(false)}
          onConfirm={canAdminResetApprovals ? doAcceptResetApprovals : doRequestReapprove}
          title={canAdminResetApprovals ? "Reset Approvals" : "Request Reapprove"}
          message={
            <div className="space-y-3 text-sm">
              {canAdminResetApprovals ? (
                <>
                  {hasResetRequest && (
                    <p>
                      <strong>{resetRequestUser || "A user"}</strong> has requested a reset of all
                      approvals.
                    </p>
                  )}
                  <p>
                    This will clear Area Manager, Payroll, and Admin approvals back to{" "}
                    <strong>Pending</strong> and dismiss any pending reset request.
                  </p>
                  <p>Are you sure you want to continue?</p>
                </>
              ) : (
                <>
                  <p>
                    This will send a request to Admin or Payroll to reset all approvals (Area
                    Manager, Payroll, Admin) for this record.
                  </p>
                  <p>
                    Existing approvals stay in place until the request is accepted. You will not be
                    able to edit the form until that happens.
                  </p>
                </>
              )}
            </div>
          }
          confirmText={canAdminResetApprovals ? "Reset Approvals" : "Send Request"}
          cancelText="Cancel"
          confirmVariant="primary"
          isLoading={isLoading}
        />

        <Modal
          isOpen={pdfLabCostMismatchOpen}
          onClose={() => setPdfLabCostMismatchOpen(false)}
          title="PDF Lab Cost Mismatch"
          size="lg"
          footer={
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPdfLabCostMismatchOpen(false)}
            >
              Close
            </Button>
          }
        >
          <div className="space-y-4 text-sm text-palette-slate-heading">
            <div>
              <div className="font-medium text-palette-slate-primary">
                {practitionerName || "Selected practitioner"} - {month}
              </div>
              <div className="mt-1 text-xs text-palette-slate-secondary">
                PDF totals use the assigned invoice total, not cost breakdown rows.
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
                <div className="text-xs text-palette-slate-secondary">Monthly Lab Costs</div>
                <div className="font-semibold text-palette-slate-primary">
                  {formatMoney(toNumber(vals.lab || 0))}
                </div>
              </div>
              <div className="rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
                <div className="text-xs text-palette-slate-secondary">PDF Lab Costs</div>
                <div className="font-semibold text-palette-slate-primary">
                  {formatMoney(selectedPdfLabCost)}
                </div>
              </div>
              <div className="rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
                <div className="text-xs text-palette-slate-secondary">Daily Reports Lab Costs</div>
                <div className="font-semibold text-palette-slate-primary">
                  {formatMoney(selectedDailyLabCostTotal)}
                </div>
              </div>
            </div>

            {selectedPdfLabCostMismatchRows.length === 0 ? (
              <div className="rounded-md border border-palette-amber-border bg-palette-amber-soft px-3 py-2 text-palette-amber-text">
                No per-day PDF vs Daily Lab Costs mismatch was found. Check the monthly Lab Costs
                entry against the PDF total.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-palette-slate-divider">
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr] bg-palette-slate-soft px-3 py-2 text-xs font-semibold uppercase text-palette-slate-secondary">
                  <div>Date</div>
                  <div className="text-right">PDF</div>
                  <div className="text-right">Daily</div>
                  <div className="text-right">Difference</div>
                </div>
                {selectedPdfLabCostMismatchRows.map((row) => (
                  <button
                    key={row.date}
                    type="button"
                    className="block w-full border-t border-palette-slate-muted px-3 py-2 text-left text-sm transition-colors hover:bg-palette-indigo-soft focus:bg-palette-indigo-soft focus:outline-none focus:ring-2 focus:ring-inset focus:ring-palette-indigo-label"
                    onClick={() => {
                      setPdfLabCostMismatchOpen(false);
                      onOpenDailyDate?.(row.date, activePid);
                    }}
                  >
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr] items-center gap-2">
                      <div className="font-medium text-palette-indigo-heading underline decoration-palette-indigo-border underline-offset-2">
                        {row.date}
                      </div>
                      <div className="text-right">{formatMoney(row.pdfAmount)}</div>
                      <div className="text-right">{formatMoney(row.dailyAmount)}</div>
                      <div className="text-right font-medium text-palette-rose-heading">
                        {formatMoney(row.difference)}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-palette-slate-secondary">
                      {row.invoiceCount} PDF invoice{row.invoiceCount === 1 ? "" : "s"}
                      {row.filenames.length > 0 ? `: ${row.filenames.slice(0, 3).join(", ")}` : ""}
                      {row.filenames.length > 3 ? "..." : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Modal>

        <div className="grid gap-6 lg:grid-cols-12 items-start">
          <div className="lg:col-span-4">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-palette-slate-badge">
                  Performance Report
                </div>
                <div className="text-lg font-bold text-palette-slate-primary leading-tight">
                  {clinicId}&apos;s Monthly Reconciliation
                </div>
              </div>

              <div className="min-w-[140px]">
                <Filter
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  options={[
                    { value: "ALL", label: "All" },
                    { value: "DDS", label: "DDS Only" },
                    { value: "HYG", label: "HYG Only" },
                  ]}
                />
              </div>
            </div>

            <Card className="shadow-none">
              {hasClinicClosure && (
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
                  <span className="text-xs font-semibold text-palette-slate-badge">
                    🔒 Clinic Closure
                  </span>
                  <span className="text-xs text-palette-slate-placeholder">
                    A scheduled closure falls within this month.
                  </span>
                </div>
              )}
              <ScrollBox height={420}>
                <ListPractitioner
                  practitioners={filteredRoster}
                  selectedId={activePid}
                  onSelect={setActivePid}
                  reportedIds={reportedIds}
                  onQuickLook={openQuickLook}
                  onVacationIds={onVacationIds}
                />
              </ScrollBox>
            </Card>
          </div>

          <div className="lg:col-span-3">
            <FormSubmit onSubmit={handleSubmit} {...formTrackingProps}>
              {(hasSubmittedRecord || isAreaManagerUser) && (
                <div
                  className={`mb-3 rounded-lg px-3 py-2 text-sm font-medium ${
                    hasSubmittedRecord
                      ? isApproved
                        ? "border border-palette-green-border bg-palette-green-soft text-palette-green-hover"
                        : "border border-palette-amber-border bg-palette-amber-soft text-palette-amber-heading"
                      : "border border-palette-gray-divider bg-palette-gray-soft text-palette-gray-badge"
                  }`}
                >
                  {hasSubmittedRecord ? (
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="font-semibold">Status:</span>
                        <ApprovalBadge label="Area Manager" approved={isAmApproved} />
                        <ApprovalBadge label="Payroll" approved={isPayrollApproved} />
                        <ApprovalBadge label="Admin" approved={isAdminApproved} />
                      </div>
                      {hasResetRequest && (
                        <div className="text-xs text-palette-amber-heading">
                          <span className="font-semibold">Reset requested</span>
                          {resetRequestUser ? ` by ${resetRequestUser}` : ""} — awaiting Admin
                          approval.
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>Status: No submission yet</span>
                  )}
                </div>
              )}

              <CostProductionBreakdownForm
                values={vals}
                breakdowns={breakdowns}
                onBreakdownChange={handleBreakdownChange}
                errors={errors}
                showErrors={hasSubmitted}
                disabled={fieldsDisabled}
                practitioner={selectedPractitioner}
                hiddenColumns={hiddenColumns}
                includedAdjustmentDeduction={includedAdjustmentDeduction}
                onIncludedAdjustmentDeductionChange={setIncludedAdjustmentDeduction}
                singleInput={clinicId !== "ON-W-ERD"}
                pdfSuggestedLabCost={selectedPdfLabCost > 0 ? selectedPdfLabCost.toFixed(2) : ""}
                pdfSuggestedLabCostComment={selectedPdfLabCostComment}
                pdfSuggestedLabCostWarning={selectedPdfLabCostWarning}
                onPdfSuggestedLabCostWarningClick={
                  selectedPdfLabCostWarning ? () => setPdfLabCostMismatchOpen(true) : undefined
                }
                extraFieldsBeforeFees={
                  clinicId === "ON-K-KIT" ? (
                    <>
                      <FormField
                        label="Anesthesiologist Production"
                        type="number"
                        value={vals.anesthesiologist_production}
                        onChange={(e) =>
                          setVals((prev) => ({
                            ...prev,
                            anesthesiologist_production: e.target.value,
                          }))
                        }
                        disabled={fieldsDisabled}
                        error={errors.anesthesiologist_production}
                        showError={hasSubmitted}
                      />
                      <FormField
                        label="Facility Production"
                        type="number"
                        value={vals.facility_production}
                        onChange={(e) =>
                          setVals((prev) => ({
                            ...prev,
                            facility_production: e.target.value,
                          }))
                        }
                        disabled={fieldsDisabled}
                        error={errors.facility_production}
                        showError={hasSubmitted}
                      />
                    </>
                  ) : null
                }
              />

              {hasOtherProfitTiers && (
                <OtherProfitTierInputs
                  tiers={otherProfitTiers}
                  values={otherProfitInputs}
                  onChange={setOtherProfitInputs}
                  errors={otherProfitErrors}
                  showErrors={hasSubmitted}
                  disabled={fieldsDisabled}
                />
              )}

              {hasOtherDeductionTiers && (
                <OtherDeductionTierInputs
                  tiers={otherDeductionTiers}
                  values={otherDeductionInputs}
                  onChange={setOtherDeductionInputs}
                  errors={otherDeductionErrors}
                  showErrors={hasSubmitted}
                  disabled={fieldsDisabled}
                />
              )}

              {isPerDayEnabled && (
                <div className="mt-3">
                  <FormField
                    label="Total Days Worked"
                    type="number"
                    value={perDayNumber}
                    onChange={(e) => setPerDayNumber(e.target.value)}
                    placeholder="e.g. 12"
                    disabled={fieldsDisabled}
                  />
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                {/*
                 * Approval-reset flow:
                 *   - Non-admin approvers (AM / Payroll / PL) who have signed
                 *     off at their level send a reset request via
                 *     `request_reset_approval`. Once a request is pending
                 *     they see a disabled "Reset Requested" indicator.
                 *   - Admin / Org sees "Accept Reset Request" when a request
                 *     is pending, or "Reset Approvals" when admin-approved.
                 *     Confirming clears all three approval levels.
                 */}
                {!isEditing && canRequestReapprove && (
                  <Button
                    type="button"
                    onClick={() => setReapproveOpen(true)}
                    variant="primary"
                    disabled={dp102Blocked}
                  >
                    Request Reapprove
                  </Button>
                )}

                {!isEditing && hasResetRequest && isAreaManagerUser && (
                  <Button type="button" variant="secondary" disabled>
                    Reset Requested
                  </Button>
                )}

                {!isEditing && canAdminResetApprovals && (
                  <Button
                    type="button"
                    onClick={() => setReapproveOpen(true)}
                    variant={hasResetRequest ? "success" : "primary"}
                    disabled={dp102Blocked}
                  >
                    {hasResetRequest ? "Accept Reset Request" : "Reset Approvals"}
                  </Button>
                )}

                {!isEditing && isAdminOrOrganizer && hasSubmittedRecord && !isApproved && (
                  <>
                    <Button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      variant="primary"
                      disabled={dp102Blocked}
                    >
                      Edit
                    </Button>

                    <Button
                      type="button"
                      onClick={() => setApproveOpen(true)}
                      variant="success"
                      disabled={dp102Blocked}
                    >
                      Approve
                    </Button>
                  </>
                )}

                {!isEditing && !isApproved && hasSubmittedRecord && canPractitionerLeadEdit && (
                  <Button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    variant="primary"
                    disabled={dp102Blocked}
                  >
                    Edit
                  </Button>
                )}

                {!isEditing && isAreaManagerUser && hasSubmittedRecord && !isAmApproved && (
                  <>
                    {!isApproved && (
                      <Button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        variant="primary"
                        disabled={dp102Blocked}
                      >
                        Edit
                      </Button>
                    )}

                    <Button
                      type="button"
                      onClick={() => setApproveOpen(true)}
                      variant="success"
                      disabled={dp102Blocked}
                    >
                      Approve
                    </Button>
                  </>
                )}

                {!isEditing && canPayrollApprove && (
                  <Button
                    type="button"
                    onClick={() => setApproveOpen(true)}
                    variant="success"
                    disabled={dp102Blocked}
                  >
                    Approve
                  </Button>
                )}

                {isEditing && (
                  <>
                    <Button
                      type="button"
                      onClick={() => {
                        setHasSubmitted(false);
                        setApproveOpen(false);
                        setIsEditing(false);
                      }}
                      variant="secondary"
                      disabled={dp102Blocked}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      disabled={dp102Blocked || !canSubmit || isLoading}
                      variant="success"
                    >
                      {submitButtonText}
                    </Button>
                  </>
                )}
              </div>
            </FormSubmit>
          </div>

          <div className="lg:col-span-5">
            <TotalMonthData
              clinicId={clinicId}
              month={month}
              practitionerId={activePid}
              practitionerName={practitionerName}
              practitionerTypeUi={practitionerTypeUi}
              data={totalMonthQuery.data}
              loading={totalMonthQuery.isLoading}
              error={totalMonthQuery.isError ? "Failed to load Total Data totals" : ""}
              fallbackReport={existingReport}
              hiddenColumns={hiddenColumns}
              pdfSuggestedLabCost={selectedPdfLabCost}
              onPdfSuggestedLabCostClick={
                selectedPdfLabCost > 0 ? () => setPdfLabCostMismatchOpen(true) : undefined
              }
            />
          </div>
        </div>
      </Card>

      {/* Read-only summary table below the Performance Report form: one row
        per practitioner with the same fields the form captures, plus a
        column-totals footer row. Column defs / totals come from
        `@utils/monthlyReconciliation` (mirrors the AssociateFee pattern). */}
      <Card className="mt-6">
        <div className="mb-3">
          <div className="text-sm font-semibold text-palette-slate-badge">
            Monthly Reconciliation
          </div>
          <div className="text-xs text-palette-slate-secondary">
            All practitioners for this clinic + month. Column totals are shown in the top row.
          </div>
        </div>
        <PaginatedTable
          columns={getMonthlyReconColumns({ hiddenColumns })}
          data={
            monthlyRecordsWithPdfLabCosts.length > 0
              ? [
                  buildMonthlyReconTotalRow(monthlyRecordsWithPdfLabCosts),
                  ...monthlyRecordsWithPdfLabCosts,
                ]
              : monthlyRecordsWithPdfLabCosts
          }
          rowKey={(row) =>
            row?.__isTotal
              ? "__monthly_recon_total__"
              : (row?.id ?? row?.practitioner_id ?? row?.practitioner_name)
          }
          emptyMessage={
            monthlyListRes?.isLoading ? "Loading\u2026" : "No monthly reconciliation records yet."
          }
          page={1}
          pageSize={Math.max(monthlyRecords.length + 1 || 1, 1)}
          total={monthlyRecords.length + (monthlyRecords.length > 0 ? 1 : 0)}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          rowClassName={() => ""}
        />
      </Card>
    </>
  );
}
